import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { InlineLoadSkeleton } from "./common/InlineLoadSkeleton";
import { clientService } from "../services/clientService";
import { userService } from "../services/userService";
import toast from "react-hot-toast";

const AddUserForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    department: "",
    role: "",
    client_id: "",
  });

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const departments = ["QA", "Product", "Dev", "Support"];
  const roles = [
    { value: "chief-admin", label: "Chief Admin" },
    { value: "project_manager", label: "Project Manager" },
    { value: "test_manager", label: "Test Manager" },
    { value: "team_lead", label: "Team Lead" },
    { value: "qa_engineer", label: "QA_Engineer" },
  ];

  // Load clients when role is not chief-admin
  useEffect(() => {
    if (formData.role && formData.role !== "chief-admin") {
      loadClients();
    } else {
      setClients([]);
      setFormData((prev) => ({ ...prev, client_id: "" }));
    }
  }, [formData.role]);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      // Try dropdown endpoint first
      try {
        const dropdownResponse = await clientService.getClientsDropdown();
        if (dropdownResponse.success && dropdownResponse.data?.clients) {
          setClients(dropdownResponse.data.clients);
          return;
        }
      } catch (err) {
        console.log("Dropdown endpoint not available, trying list endpoint");
      }

      // Fallback to list endpoint
      const response = await clientService.getClients({
        page: 1,
        limit: 1000,
        is_active: true,
        sort_by: "client_name",
        sort_order: "asc",
      });

      if (response.success && response.data?.clients) {
        setClients(response.data.clients);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    // Department is optional for admin role, but required for other roles
    if (
      formData.role !== "chief-admin" &&
      formData.role !== "admin" &&
      !formData.department
    ) {
      newErrors.department = "Department is required";
    }

    // Client is required for non-chief-admin roles
    if (formData.role !== "chief-admin" && !formData.client_id) {
      newErrors.client_id = "Client is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Reset client_id when role changes to chief-admin
    if (field === "role" && value === "chief-admin") {
      setFormData((prev) => ({ ...prev, client_id: "", department: "" }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name)(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const userData = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim().toLowerCase(),
        role: formData.role,
      };

      // Add department and client_id only for non-chief-admin roles
      if (formData.role !== "chief-admin") {
        userData.department = formData.department;
        userData.client_id = formData.client_id;
      }

      await userService.createUser(userData);
      toast.success("User created successfully");

      // Reset form
      setFormData({
        full_name: "",
        email: "",
        department: "",
        role: "",
        client_id: "",
      });
      setErrors({});

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating user:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create user";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Full Name */}
      <div>
        <Label htmlFor="full_name">Full Name *</Label>
        <Input
          id="full_name"
          name="full_name"
          value={formData.full_name}
          onChange={handleInputChange}
          placeholder="Enter full name"
          className={errors.full_name ? "border-red-500" : ""}
        />
        {errors.full_name && (
          <p className="text-sm text-red-500 mt-1">{errors.full_name}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          placeholder="Enter email address"
          className={errors.email ? "border-red-500" : ""}
        />
        {errors.email && (
          <p className="text-sm text-red-500 mt-1">{errors.email}</p>
        )}
      </div>

      {/* Role */}
      <div>
        <Label htmlFor="role">Role *</Label>
        <Select value={formData.role} onValueChange={handleChange("role")}>
          <SelectTrigger className={errors.role ? "border-red-500" : ""}>
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            {roles.map((role) => (
              <SelectItem key={role.value} value={role.value}>
                {role.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.role && (
          <p className="text-sm text-red-500 mt-1">{errors.role}</p>
        )}
      </div>

      {/* Department - Only show for non-chief-admin roles, optional for admin */}
      {formData.role && formData.role !== "chief-admin" && (
        <div>
          <Label htmlFor="department">
            Department {formData.role !== "admin" ? "*" : ""}
          </Label>
          <Select
            value={formData.department}
            onValueChange={handleChange("department")}
          >
            <SelectTrigger
              className={errors.department ? "border-red-500" : ""}
            >
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept} value={dept}>
                  {dept}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.department && (
            <p className="text-sm text-red-500 mt-1">{errors.department}</p>
          )}
        </div>
      )}

      {/* Client - Only show for non-chief-admin roles */}
      {formData.role && formData.role !== "chief-admin" && (
        <div>
          <Label htmlFor="client_id">Client *</Label>
          <Select
            value={formData.client_id}
            onValueChange={handleChange("client_id")}
            disabled={loadingClients}
          >
            <SelectTrigger className={errors.client_id ? "border-red-500" : ""}>
              <SelectValue
                placeholder={loadingClients ? "—" : "Select client"}
              />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.client_id} value={client.client_id}>
                  {client.client_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.client_id && (
            <p className="text-sm text-red-500 mt-1">{errors.client_id}</p>
          )}
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <InlineLoadSkeleton className="h-4 w-4 mr-2" />
              Creating...
            </>
          ) : (
            "Create User"
          )}
        </Button>
      </div>
    </form>
  );
};

export default AddUserForm;
